import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../axiosConfig";
import EchipamentPickerModal from "../../pickers/EchipamentPickerModal";

import CounterHistory from "./CounterHistory";

export default function SesizareForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  // Form Fields
  const [bxdoc, setBxdoc] = useState("");
  const [bxdata, setBxdata] = useState("");
  const [echipamentId, setEchipamentId] = useState(null);
  const [echipamentLabel, setEchipamentLabel] = useState("Alege echipament...");

  const [tipEvenimentId, setTipEvenimentId] = useState("");
  const [angajatId, setAngajatId] = useState("");
  const [bxdescr, setBxdescr] = useState("");
  const [bxfinalizat, setBxfinalizat] = useState(false);
  const [facturabil, setFacturabil] = useState(false);
  const [facturat, setFacturat] = useState(false);
  const [m1, setM1] = useState("0");
  const [m2, setM2] = useState("0");
  const [m3, setM3] = useState("0");
  const [m4, setM4] = useState("0");
  const [m5, setM5] = useState("0");
  const [deFacturat, setDeFacturat] = useState("0.0");
  const [bxnumeClient, setBxnumeClient] = useState("");
  const [locatie, setLocatie] = useState("");
  const [branchId, setBranchId] = useState("");

  // Nomenclator lists
  const [tipuriEveniment, setTipuriEveniment] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  // Pickers state
  const [echipamentPickerOpen, setEchipamentPickerOpen] = useState(false);

  useEffect(() => {
    // Set default date to now
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    setBxdata(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`);

    loadNomenclators();
    if (isEdit) {
      loadSesizare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadNomenclators = async () => {
    try {
      const [rEvs, rUsers, rBranches] = await Promise.all([
        api.get("/contor/nomenclatoare/tipuri-eveniment"),
        api.get("/contor/nomenclatoare/utilizatori", { params: { limit: 100 } }),
        api.get("/branches/", { params: { limit: 200 } }),
      ]);
      setTipuriEveniment(rEvs.data || []);
      setUsers(rUsers.data?.items || []);
      setBranches(rBranches.data?.items || []);
    } catch (e) {
      console.error("Eroare la încărcarea nomenclatoarelor:", e);
    }
  };

  const loadSesizare = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/contor/sesizari/${id}`);
      const data = res.data;

      setBxdoc(data.bxdoc);
      if (data.bxdata) {
        setBxdata(data.bxdata.substring(0, 16));
      }
      setEchipamentId(data.bxechipament_id);
      setEchipamentLabel(data.echipament_seria ? `${data.echipament_seria} • ${data.echipament_descr || ""}` : "Alege echipament...");
      setTipEvenimentId(data.bxtipeveniment_id || "");
      setAngajatId(data.bxangajat_id || "");
      setBxdescr(data.bxdescr || "");
      setBxfinalizat(data.bxfinalizat === 1);
      setFacturabil(data.facturabil === 1);
      setFacturat(data.facturat === 1);
      setM1(String(data.m1 ?? 0));
      setM2(String(data.m2 ?? 0));
      setM3(String(data.m3 ?? 0));
      setM4(String(data.m4 ?? 0));
      setM5(String(data.m5 ?? 0));
      setDeFacturat(String(data.de_facturat ?? 0.0));
      setBxnumeClient(data.bxnume_client || "");
      setLocatie(data.locatie || "");
      setBranchId(data.branch_id ? String(data.branch_id) : "");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Eroare la încărcarea sesizării.");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const errs = {};
    if ([m1, m2, m3, m4, m5].some(v => v === "" || !Number.isSafeInteger(Number(v)) || Number(v) < 0 || Number(v) > 2147483647)) {
      setError("Contoarele trebuie să fie numere întregi pozitive sau zero.");
      return false;
    }
    if (!bxdata) errs.bxdata = "Data este obligatorie.";
    if (!echipamentId) errs.echipament = "Echipamentul este obligatoriu.";
    if (!tipEvenimentId) errs.tipEveniment = "Tipul de eveniment este obligatoriu.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handlePickEchipament = (eq) => {
    // eq is from EchipamentPickerModal: the raw Bxechipament object
    setEchipamentId(eq.bxid);
    setEchipamentLabel(`${eq.bxseria} • ${eq.bxdescrechip || ""}`);
    
    // Autofill metadata from equipment
    setBxnumeClient(eq.utilizator_name || "");
    setLocatie(eq.bxlocatie || "");
    setBranchId(eq.branch_id ? String(eq.branch_id) : "");

    if (errors.echipament) setErrors({ ...errors, echipament: null });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setError("");

    const payload = {
      bxechipament_id: Number(echipamentId),
      bxdata,
      bxtipeveniment_id: Number(tipEvenimentId),
      bxangajat_id: angajatId ? Number(angajatId) : null,
      bxdescr: bxdescr.trim() || null,
      bxfinalizat: bxfinalizat ? 1 : 0,
      facturabil: facturabil ? 1 : 0,
      facturat: facturat ? 1 : 0,
      m1: Number(m1),
      m2: Number(m2),
      m3: Number(m3),
      m4: Number(m4),
      m5: Number(m5),
      de_facturat: parseFloat(deFacturat) || 0.0,
      bxnume_client: bxnumeClient.trim() || null,
      locatie: locatie.trim() || null,
      branch_id: branchId ? Number(branchId) : null,
    };

    if (isEdit) {
      payload.bxdoc = bxdoc;
    }

    try {
      if (isEdit) {
        await api.put(`/contor/sesizari/${id}`, payload);
        toast.success("Sesizarea a fost actualizată.");
      } else {
        await api.post("/contor/sesizari", payload);
        toast.success("Sesizarea a fost deschisă cu succes.");
      }
      navigate("/contor/sesizari");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Eroare la salvare.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Se încarcă datele solicitării...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1 text-dark fw-bold">
            {isEdit ? `Editează Tichet: ${bxdoc}` : "Deschide Sesizare Nouă"}
          </h2>
          <p className="text-muted mb-0">
            {isEdit ? "Modifică detaliile tichetului de asistență service" : "Înregistrează o solicitare de intervenție sau citire nouă"}
          </p>
        </div>
        <Button variant="outline-secondary" onClick={() => navigate("/contor/sesizari")}>
          Înapoi la Listă
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSave}>
        <Row className="g-4">
          {/* Stânga: Detalii tichet și diagnostic */}
          <Col xs={12} lg={8}>
            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-light fw-bold py-3 text-dark">Informații Intervenție</Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3">
                  {isEdit && (
                    <Col xs={12} md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Număr Document</Form.Label>
                        <Form.Control value={bxdoc} readOnly className="bg-light fw-bold" />
                      </Form.Group>
                    </Col>
                  )}

                  <Col xs={12} md={isEdit ? 8 : 12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Data Solicitare <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={bxdata}
                        onChange={(e) => {
                          setBxdata(e.target.value);
                          if (errors.bxdata) setErrors({ ...errors, bxdata: null });
                        }}
                        isInvalid={!!errors.bxdata}
                      />
                      <Form.Control.Feedback type="invalid">{errors.bxdata}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Echipament Service <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          value={echipamentLabel}
                          readOnly
                          isInvalid={!!errors.echipament}
                          className="bg-light fw-semibold"
                        />
                        <Button
                          variant="outline-dark"
                          onClick={() => setEchipamentPickerOpen(true)}
                        >
                          Alege Echipament
                        </Button>
                      </div>
                      {errors.echipament && <div className="text-danger small mt-1">{errors.echipament}</div>}
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Descriere defect raportat / Constatări diagnostic</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        value={bxdescr}
                        onChange={(e) => setBxdescr(e.target.value)}
                        placeholder="Descrieți simptomele defectului, diagnosticarea tehnică și reparațiile efectuate..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-light fw-bold py-3 text-dark">Citiri noi ale contoarelor (M1–M5)</Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3">
                  <Col xs={6} md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Contor M1</Form.Label>
                      <Form.Control
                        type="number"
                        value={m1}
                        onChange={(e) => setM1(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Contor M2</Form.Label>
                      <Form.Control
                        type="number"
                        value={m2}
                        onChange={(e) => setM2(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Contor M3</Form.Label>
                      <Form.Control
                        type="number"
                        value={m3}
                        onChange={(e) => setM3(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Contor M4</Form.Label>
                      <Form.Control
                        type="number"
                        value={m4}
                        onChange={(e) => setM4(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={3}><Form.Group>
                    <Form.Label>Contor M5 / Scanări</Form.Label>
                    <Form.Control type="number" min="0" step="1" value={m5} onChange={e => setM5(e.target.value)} />
                  </Form.Group></Col>
                </Row>
                <CounterHistory id={id} />
              </Card.Body>
            </Card>
          </Col>

          {/* Dreapta: Stadiu, Preluare, Locație și Facturare */}
          <Col xs={12} lg={4}>
            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-light fw-bold py-3 text-dark">Stadiu & Responsabil</Card.Header>
              <Card.Body className="p-4">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    Tip Solicitare/Eveniment <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={tipEvenimentId}
                    onChange={(e) => {
                      setTipEvenimentId(e.target.value);
                      if (errors.tipEveniment) setErrors({ ...errors, tipEveniment: null });
                    }}
                    isInvalid={!!errors.tipEveniment}
                  >
                    <option value="">— Selectează tipul —</option>
                    {tipuriEveniment.map((te) => (
                      <option key={te.bxid} value={te.bxid}>
                        {te.bxdescr}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errors.tipEveniment}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Tehnician/Inginer repartizat</Form.Label>
                  <Form.Select
                    value={angajatId}
                    onChange={(e) => setAngajatId(e.target.value)}
                  >
                    <option value="">— Alege inginer —</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Unitate</Form.Label>
                  <Form.Select
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                  >
                    <option value="">— Alege unitatea —</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.code})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="bxfinalizat-check"
                    label="Tichet Finalizat / Rezolvat"
                    checked={bxfinalizat}
                    onChange={(e) => setBxfinalizat(e.target.checked)}
                    className="fw-semibold text-success"
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-light fw-bold py-3 text-dark">Locație & Client Beneficiar</Card.Header>
              <Card.Body className="p-4">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Nume Client Beneficiar</Form.Label>
                  <Form.Control
                    value={bxnumeClient}
                    onChange={(e) => setBxnumeClient(e.target.value)}
                    placeholder="Prefiltrare din utilizatorul echipamentului..."
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Adresă exactă locație echipament</Form.Label>
                  <Form.Control
                    value={locatie}
                    onChange={(e) => setLocatie(e.target.value)}
                    placeholder="Prefiltrare din locația echipamentului..."
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-light fw-bold py-3 text-dark">Facturare Intervenție</Card.Header>
              <Card.Body className="p-4">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="facturabil-check"
                    label="Intervenție Facturabilă (Post-garanție/T&M)"
                    checked={facturabil}
                    onChange={(e) => setFacturabil(e.target.checked)}
                    className="fw-semibold text-dark"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="facturat-check"
                    label="Factură Emisă"
                    checked={facturat}
                    onChange={(e) => setFacturat(e.target.checked)}
                    disabled={!facturabil}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Cost estimat de facturat</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={deFacturat}
                    onChange={(e) => setDeFacturat(e.target.value)}
                    disabled={!facturabil}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2 mb-5">
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/contor/sesizari")}
            disabled={saving}
          >
            Renunță
          </Button>
          <Button variant="dark" type="submit" disabled={saving}>
            {saving ? "Se salvează..." : "Salvează Sesizarea"}
          </Button>
        </div>
      </Form>

      <EchipamentPickerModal
        show={echipamentPickerOpen}
        onHide={() => setEchipamentPickerOpen(false)}
        onPick={handlePickEchipament}
        title="Alege Echipamentul Defect"
      />
    </Container>
  );
}
